"use client";

import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Filter } from "lucide-react";
import { useIsSmallScreen } from "@/hooks/use-is-small-screen";
import { FilterItem } from "@/interfaces";
import { FilterControls } from "./FilterControls";
import { FilterSheet } from "./FilterSheet";

type FilterBarProps = {
  filters: FilterItem[];
  onReset: () => void;
};

/**
 * Helper function to count active filters
 */
const countActiveFilters = (filters: FilterItem[]): number => {
  return filters.filter((filter) => {
    if (filter.type === "search") {
      return filter.value && filter.value.trim() !== "";
    }
    if (filter.type === "date") {
      return filter.value !== null && filter.value !== undefined;
    }
    if (filter.type === "dateRange") {
      return filter.value?.from !== null && filter.value?.from !== undefined;
    }
    if (filter.type === "select" || filter.type === "combobox") {
      return filter.value && filter.value !== "";
    }
    return false;
  }).length;
};

export const FilterBar = ({ filters, onReset }: FilterBarProps) => {
  const isSmallScreen = useIsSmallScreen();
  const [sheetOpen, setSheetOpen] = useState(false);

  const activeFilterCount = useMemo(() => countActiveFilters(filters), [filters]);

  // Handler to clear individual filter
  const handleClearFilter = (index: number) => {
    const filter = filters[index];
    if (filter) {
      // Reset the filter value based on its type
      if (filter.type === "search") {
        filter.onValueChange("");
      } else if (filter.type === "date") {
        filter.onValueChange(null);
      } else if (filter.type === "dateRange") {
        filter.onValueChange({ from: undefined, to: undefined });
      } else if (filter.type === "select" || filter.type === "combobox") {
        filter.onValueChange("");
      }
    }
  };

  // For small screens (xs, sm, md) - show button + sheet
  if (isSmallScreen) {
    return (
      <>
        <div className="pb-6">
          <Button variant="outline" onClick={() => setSheetOpen(true)} className="w-full sm:w-auto">
            <Filter className="w-4 h-4 mr-2" />
            Filters
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="ml-2">
                {activeFilterCount}
              </Badge>
            )}
          </Button>
        </div>
        <FilterSheet
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          filters={filters}
          onReset={onReset}
          onClearFilter={handleClearFilter}
        />
      </>
    );
  }

  // For large screens (lg+) - show inline filters
  return (
    <div className="space-y-4 pb-6">
      <FilterControls filters={filters} showActions={true} onReset={onReset} onClearFilter={handleClearFilter} />
    </div>
  );
};
